// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Chip, makeStyles, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import classNames from 'classnames';
import { toast } from 'react-toastify';

// Internal Dependencies
import { EscapeSingleQuote, StringToLowercase, Trim } from '../../common/util';
import useDebounce from '../global/use-debounce';
import apiService from '../../services.js/apiService';
import Colours from '../../styles/colours';
import { IsEmpty, IsString } from '../../common/checks';
import Constants from '../../common/constants';

const useStyles = makeStyles(() => ({
  approvedTag: {
    backgroundColor: '#2ecc71',
    color:           '#FFFFFF',
  },
  rejectedTag: {
    backgroundColor: '#e74c3c',
    color:           '#FFFFFF',
  },
  pendingTag: {
    backgroundColor: '#95a5a6',
    color:           '#FFFFFF',
  },
}));

const Tagging = (props) => {
  const classes = useStyles();
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [addedTags, setAddedTags] = useState([]);
  const [tagInput, setTagInput] = useState('');

  const debouncedTagInput = useDebounce(tagInput, 500);

  const removableMap = Object.fromEntries(props.data.map((d) => [d.name, !!d.removable]));
  const labelList = props.data.map((d) => d.name);

  const findTagsOfDataAndSetAsAddedTags = async (data) => {
    try {
      // format the tag names needed by server
      const tagNamesDelimited = _.join(data, '||');
      const tags = await apiService.getTagsByNames({ tagNames: tagNamesDelimited });
      const displayTags = [];
      for (const d of data) {
        let found = false;
        for (const t of tags) {
          if (d === t.name) {
            found = true;
            displayTags.push(t);
            break;
          }
        }
        if (!found) {
          displayTags.push({ name: d, isApproved: null });
        }
      }
      setAddedTags(displayTags);
    } catch (error) {
      toast.error(error);
    }
  };

  useEffect(() => {
    if (IsEmpty(labelList)) {
      setAddedTags([]);
    } else {
      // Note: We are expecting labelList to be an array of String of the tag names
      // Get the tag status of each item in the data
      findTagsOfDataAndSetAsAddedTags(labelList);
    }
  }, [props.data]);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        if (!IsEmpty(Trim(debouncedTagInput))) {
          const nameSearch = EscapeSingleQuote(Trim(debouncedTagInput));
          const result = await apiService.getTags({ name: nameSearch, pageNo: 0, pageSize: 20 });
          const uniqueTags = _.compact(_.map(result.rows, (tag) => (!labelList.includes(tag.name) ? tag : false)));

          setSuggestedTags(uniqueTags || []);
        } else {
          setSuggestedTags([]);
        }
      } catch (error) {
        toast.error(error);
      }
    };
    fetchTags();
  }, [debouncedTagInput]);

  const findAndRemoveTag = (values, tagName = '') => {
    // user deleted one of the partner. Find out which is deleted
    const existingTags = labelList.slice();
    for (const index in existingTags) {
      if (!IsEmpty(index)) {
        const currentTag = existingTags[index];
        let deleted = true;
        if (tagName) {
          deleted = tagName === currentTag;
        } else {
          for (const value of values) {
            if (value.name === currentTag) {
              deleted = false;
              break;
            }
          }
        }
        if (deleted && !removableMap[currentTag]) {
          // found the partner index to delete
          existingTags.splice(index, 1);
          break;
        }
      }
    }
    // update parent com
    props.onChange(existingTags);

    // Code below is to update items displayed in the UI
    const existingAddedTags = addedTags.slice();
    for (const index in existingAddedTags) {
      if (!IsEmpty(index)) {
        const currentTag = existingAddedTags[index].name;
        let deleted = true;
        if (tagName) {
          deleted = tagName === currentTag;
        } else {
          for (const value of values) {
            if (value.name === currentTag) {
              deleted = false;
              break;
            }
          }
        }
        if (deleted && !removableMap[currentTag]) {
          // found the index to delete
          existingAddedTags.splice(index, 1);
          break;
        }
      }
    }
    setAddedTags(existingAddedTags);
  };

  const addValueIfNew = async (value) => {
    try {
      value = IsString(value) ? StringToLowercase(value) : StringToLowercase(value.name);
      const existingTags = labelList.slice();
      let found = false;

      // handle case where user hit 'Enter', which causes input 'value' to be a string
      const enteredValue = IsString(value) ? value : value.name;

      if (IsEmpty(Trim(enteredValue, false, true))) {
        // ignore empty string
        return;
      }

      for (const tag of existingTags) {
        if (tag === enteredValue) {
          found = true;
          break;
        }
      }

      if (!found) {
        // add and update parent com
        existingTags.push(enteredValue);
        props.onChange(existingTags);

        // update UI
        // handle case where user hit 'Enter', which causes input 'value' to be a string
        if (IsString(value)) {
          const tags = await apiService.getTagsByNames({ tagNames: value });
          if (IsEmpty(tags)) {
            value = { name: value, isApproved: null };
          } else {
            value = tags[0];
          }
        }
        setAddedTags([...addedTags.slice(), value]);
      }
    } catch (error) {
      toast.error('Error in addValueIfNew(): ', error);
    }
  };

  const onBlur = (value) => {
    addValueIfNew(value);
  };

  const handleChanges = (values) => {
    try {
      // values will contain the distinct and updated array
      // Note that the data is an array of { name, isApproved }
      if (IsEmpty(values)) {
        props.onChange(labelList.filter((tag) => removableMap[tag]));
        setAddedTags(addedTags.filter((tag) => removableMap[tag.name]));
      } else if (values.length > labelList.length) {
        // new value added will always be the last time
        addValueIfNew(values[values.length - 1]);
      } else {
        findAndRemoveTag(values);
      }
    } catch (err) {
      toast.error(Constants.Error.ReportToIT);
      toast.error(err);
    }
  };

  const handleDelete = (value) => {
    try {
      if (props.halal && value.name === 'halal') toast.error(`halal tag cannot be deleted because the link page has halal status ${props.halal}`);
      else if (StringToLowercase(props.halal) === value.name) toast.error(`${props.halal} tag cannot be deleted because the link page has halal status ${props.halal}`);
      else if (props.persistTag === value.name) toast.error(`Place name ( ${props.persistTag} ) tag cannot be deleted`);
      else findAndRemoveTag([], value.name);
    } catch (err) {
      toast.error(Constants.Error.ReportToIT);
      toast.error(err);
    }
  };

  return (
    <div className="d-flex flex-column">
      <Autocomplete
        limitTags={props.limitTags}
        multiple
        options={suggestedTags}
        getOptionLabel={(option) => option.name}
        value={addedTags}
        inputValue={tagInput}
        disableCloseOnSelect
        filterSelectedOptions
        freeSolo
        onInputChange={(event, value) => setTagInput(IsEmpty(value) ? '' : value)}
        onKeyDown={props.onKeyDown}
        renderOption={(option) => (
          <>
            <span
              classes={{
                root: classNames(classes.tag, {
                  [classes.approvedTag]: option.isApproved === true,
                  [classes.rejectedTag]: option.isApproved === false,
                  [classes.pendingTag]:  option.isApproved === null,
                }),
              }}
              style={{
                background:
                  option.isApproved === true ? Colours.Green
                    : option.isApproved === false ? Colours.Red
                      : Colours.Gray1,
                color:        Colours.White,
                marginRight:  15,
                width:        10,
                height:       10,
                borderRadius: 10,
              }}
            />
            {option.name}
          </>
        )}
        renderTags={(value, getTagProps) => value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            variant="outlined"
            label={option.name}
            classes={{
              root: classNames(classes.tag, {
                [classes.approvedTag]: option.isApproved === true,
                [classes.rejectedTag]: option.isApproved === false,
                [classes.pendingTag]:  option.isApproved === null,
              }),
            }}
            style={{ borderColor: Colours.White, fontSize: 15 }}
            onDelete={removableMap[option.name] || props.disabled ? null : (() => handleDelete(option))}
          />
        ))}
        onChange={(event, values) => handleChanges(values)}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={props.disabled ? '' : "Type and hit 'Enter' to add"}
            onBlur={() => onBlur(params.inputProps.value)}
            style={{
              backgroundColor: props.disabled ? Colours.Gray7 : Colours.Transaparent,
            }}
          />
        )}
        disabled={props.disabled}
      />
    </div>
  );
};

Tagging.propTypes = {
  data:      PropTypes.arrayOf(PropTypes.string),
  disabled:  PropTypes.bool,
  limitTags: PropTypes.number,
  onChange:  PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  textSize:  PropTypes.oneOf([
    'h4', 'h5', 'h6', 'body1', 'body2',
  ]),
  title:      PropTypes.string,
  halal:      PropTypes.string,
  persistTag: PropTypes.string,
};

Tagging.defaultProps = {
  data:       [],
  disabled:   false,
  onKeyDown:  () => {},
  limitTags:  20,
  textSize:   'h6',
  title:      '',
  halal:      '',
  persistTag: '',
};

export default Tagging;
